import React from "react";
import WarningBanner from "@/components/ui/WarningBanner";
import AddressAutocomplete from "@/components/ui/AddressAutoComplete";
import { DeliveryErrorModal } from "../modals";
import { Address } from "@/types/branch";

interface DeliveryAddressFormProps {
  deliveryAddress: Address;
  deliveryFeeError: string;
  isDeliveryModalOpen: boolean;
  onClose: () => void;
  onAddressChange: (field: keyof Address, value: string) => void;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  closeDeliveryModal: () => void;
}

export const DeliveryAddressForm: React.FC<DeliveryAddressFormProps> = ({
  deliveryAddress,
  deliveryFeeError,
  isDeliveryModalOpen,
  onClose,
  onAddressChange,
  onPlaceSelected,
  onSubmit,
  closeDeliveryModal,
}) => {
  return (
    <div className="p-4">
      {deliveryFeeError && (
        <WarningBanner
          title="Delivery Fees Calculation Error"
          text={deliveryFeeError}
          className=""
        />
      )}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base sm:text-2xl font-bold ml-2">
          Delivery Address
        </h2>
        <button onClick={onClose} className="text-black">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <form className="space-y-4" onSubmit={onSubmit}>
        <AddressAutocomplete
          value={deliveryAddress.street || ""}
          onChange={(value: string) => onAddressChange("street", value)}
          onPlaceSelected={onPlaceSelected}
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700"
            >
              City
            </label>
            <input
              id="city"
              type="text"
              name="city"
              required
              autoComplete="city"
              value={deliveryAddress.city}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onChange={(e) => onAddressChange("city", e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="province"
              className="block text-sm font-medium text-gray-700"
            >
              State
            </label>
            <input
              id="province"
              type="text"
              name="province"
              required
              autoComplete="province"
              value={deliveryAddress.province}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onChange={(e) => onAddressChange("province", e.target.value)}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="country"
              className="block text-sm font-medium text-gray-700"
            >
              Country
            </label>
            <input
              id="country"
              type="text"
              name="country"
              required
              autoComplete="country"
              value={deliveryAddress.country}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onChange={(e) => onAddressChange("country", e.target.value)}
            />
          </div>
          <div>
            <label
              htmlFor="postal_code"
              className="block text-sm font-medium text-gray-700"
            >
              Zip Code
            </label>
            <input
              id="postal_code"
              type="text"
              name="postal_code"
              required
              autoComplete="postal_code"
              value={deliveryAddress.postal_code}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onChange={(e) => onAddressChange("postal_code", e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-center">
          <button
            type="submit"
            className="w-full py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Continue
          </button>
        </div>
      </form>
      <DeliveryErrorModal
        isOpen={isDeliveryModalOpen}
        onClose={closeDeliveryModal}
      />
    </div>
  );
};
